import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Users, Loader2, ChevronDown, ChevronRight, AlertTriangle, Euro, FileText, Download } from 'lucide-react'

const API = process.env.REACT_APP_BACKEND_URL

const fmtEuro = v => Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' €'
const fmtDate = d => d ? new Date(d).toLocaleDateString('fr-FR') : '--'

const RESP_LABEL = { responsable: 'Resp.', non_responsable: 'Non resp.', partage: 'Partage', partage_50_50: 'Partage', sans_suite: 'Sans suite' }

function TauxBadge({ taux, nb }) {
  if (!nb) return <span className="text-slate-300">--</span>
  const cls = taux >= 60 ? 'bg-red-100 text-red-700' : taux >= 30 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'
  return <span className={`px-2 py-0.5 rounded-full text-[11px] font-bold ${cls}`}>{taux}%</span>
}

function ChauffeurRow({ c, open, onToggle, navigate, basePath }) {
  const alerte = c.nb_sinistres >= 3
  return (
    <>
      <tr onClick={onToggle} className={`border-b border-slate-100 cursor-pointer hover:bg-indigo-50/40 ${alerte ? 'bg-amber-50/40' : ''}`} data-testid={`chauffeur-row-${c.cle}`}>
        <td className="px-3 py-2.5 text-slate-400">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</td>
        <td className="px-3 py-2.5">
          <span className="text-sm font-semibold text-slate-800" data-testid="chauffeur-nom-complet">{c.nom_complet || c.nom}</span>
          {alerte && <AlertTriangle size={12} className="inline ml-1.5 text-amber-500" title="3 sinistres ou plus" />}
        </td>
        <td className="px-3 py-2.5 text-xs text-slate-500">{c.depot || '--'}</td>
        <td className="px-3 py-2.5 text-center"><span className="inline-flex items-center justify-center min-w-[28px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-xs font-bold" data-testid="chauffeur-nb-sinistres">{c.nb_sinistres}</span></td>
        <td className="px-3 py-2.5 text-center text-xs text-slate-500">{c.nb_amiable}</td>
        <td className="px-3 py-2.5 text-right text-sm font-semibold text-slate-800" data-testid="chauffeur-cout">{fmtEuro(c.cout_total)}</td>
        <td className="px-3 py-2.5 text-center"><TauxBadge taux={c.taux_responsabilite} nb={c.nb_sinistres} /></td>
        <td className="px-3 py-2.5 text-xs text-slate-500">{fmtDate(c.dernier_sinistre)}</td>
      </tr>
      {open && (
        <tr className="bg-slate-50/60">
          <td colSpan={8} className="px-6 py-3">
            <div className="space-y-1">
              {c.sinistres.map(s => (
                <button key={s.id} onClick={e => { e.stopPropagation(); navigate(`${basePath}/${s.id}`) }}
                  className="w-full flex items-center justify-between gap-3 px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs hover:border-indigo-300 text-left"
                  data-testid={`chauffeur-sinistre-${s.id}`}>
                  <span className="font-mono font-bold text-indigo-700">{s.numero_sinistre_client || s.numero_sinistre}</span>
                  <span className="text-slate-500">{fmtDate(s.date_fait)}</span>
                  <span className="text-slate-600">{RESP_LABEL[s.responsabilite] || '--'}</span>
                  <span className="text-slate-500">{s.statut || ''}</span>
                  <span className="font-semibold text-slate-800">{fmtEuro(s.cout)}</span>
                </button>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  )
}

export default function SuiviChauffeursTab({ tenantId, sinistreBasePath = '/c2/sinistre' }) {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(null)
  const [q, setQ] = useState('')

  useEffect(() => {
    setLoading(true)
    fetch(`${API}/api/sinistres/suivi-chauffeurs?tenant_id=${tenantId}`)
      .then(r => r.json()).then(setData).catch(() => setData({ chauffeurs: [] }))
      .finally(() => setLoading(false))
  }, [tenantId])

  if (loading) return <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-indigo-500" /></div>

  const list = (data?.chauffeurs || []).filter(c => !q || `${c.nom_complet || c.nom} ${c.depot || ''}`.toLowerCase().includes(q.toLowerCase()))

  return (
    <div className="p-6 space-y-4" data-testid="suivi-chauffeurs-tab">
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center"><Users size={18} className="text-indigo-500" /></div>
          <div><p className="text-xs text-slate-400">Chauffeurs impliqués</p><p className="text-xl font-bold text-slate-900" data-testid="kpi-chauffeurs">{data?.chauffeurs?.length || 0}</p></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center"><FileText size={18} className="text-amber-500" /></div>
          <div><p className="text-xs text-slate-400">Sinistres flotte</p><p className="text-xl font-bold text-slate-900" data-testid="kpi-sinistres">{data?.total_sinistres || 0}</p></div>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center"><Euro size={18} className="text-emerald-500" /></div>
          <div><p className="text-xs text-slate-400">Coût total</p><p className="text-xl font-bold text-slate-900" data-testid="kpi-cout">{fmtEuro(data?.cout_total)}</p></div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
          <h3 className="text-sm font-semibold text-slate-800">Suivi par chauffeur</h3>
          <div className="flex items-center gap-2">
            <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filtrer nom, prénom, dépôt..."
              className="h-8 px-3 text-xs border border-slate-200 rounded-md outline-none focus:ring-2 focus:ring-indigo-500 w-64" data-testid="suivi-chauffeurs-search" />
            <a href={`${API}/api/sinistres/suivi-chauffeurs/export?tenant_id=${tenantId}`} download
              className="h-8 px-3 flex items-center gap-1.5 text-xs font-medium bg-emerald-600 text-white rounded-md hover:bg-emerald-700" data-testid="suivi-chauffeurs-export-btn">
              <Download size={13} /> Export Excel
            </a>
          </div>
        </div>
        {list.length === 0 ? (
          <p className="p-8 text-center text-sm text-slate-400">Aucun sinistre flotte avec chauffeur renseigné</p>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-400">
                <th className="w-8"></th>
                <th className="text-left px-3 py-2">Nom</th>
                <th className="text-left px-3 py-2">Dépôt</th>
                <th className="text-center px-3 py-2">Sinistres</th>
                <th className="text-center px-3 py-2">dont amiable</th>
                <th className="text-right px-3 py-2">Coût total</th>
                <th className="text-center px-3 py-2">Taux resp.</th>
                <th className="text-left px-3 py-2">Date de sinistre</th>
              </tr>
            </thead>
            <tbody>
              {list.map(c => <ChauffeurRow key={c.cle} c={c} open={open === c.cle} onToggle={() => setOpen(open === c.cle ? null : c.cle)} navigate={navigate} basePath={sinistreBasePath} />)}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
